/* eslint-disable @atlaskit/design-system/no-unsafe-design-token-usage */
/** @jsx jsx */
import type { FC, MutableRefObject, ReactNode } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
import { useLayoutEffect, useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { keyframes, css, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { Toolbar } from '@atlaskit/editor-toolbar';

import { expValEquals } from '@confluence/feature-experiments';
import { getElementBounds } from '@confluence/dom-helpers';
import { fg } from '@confluence/feature-gating';

import { popupPortalContainerId } from './HighlightActionsProvider.tsx';

const popupAnimation = keyframes({
	'0%': {
		opacity: '0',
	},
	'100%': {
		opacity: '1',
	},
});

const popupPanelStyles = css({
	position: 'absolute',
	animation: `${popupAnimation} 100ms forwards linear`,
});

const popupTransition = css({
	transition: 'top 100ms ease-out, left 100ms ease-out',
});

const buttonContainerStyle = css({
	transform: `translate(calc(-50% - 5px), calc(-100% - 20px))`,
	boxShadow: token('elevation.shadow.overlay'),
	borderRadius: token('radius.small', '3px'),
	position: 'absolute',
	top: '0',
	backgroundColor: token('elevation.surface.overlay'),
	display: 'flex',
});

const buttonContainerBorderUpdated = css({
	borderRadius: token('radius.small'),
});

const toolbarContainerStyle = css({
	transform: `translate(calc(-50% - 5px), calc(-100% - 20px))`,
	position: 'absolute',
	top: '0',
});

const i18n = defineMessages({
	toolbarAlert: {
		id: 'highlight-actions.toolbar.alert',
		defaultMessage: 'Floating toolbar is available. Press tab to access it',
		description:
			'Alert to announce to screenreader users how to access the floating toolbar with the keyboard',
	},
});

type PopupProps = {
	children: ReactNode;
	top: number;
	left: number;
	needTransition?: boolean;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	updatePosition?: () => undefined | Position;
};

export type Position = {
	top: number;
	left: number;
};

export const Popup: FC<PopupProps> = ({
	children,
	top,
	left,
	needTransition = true,
	onMouseLeave,
	onMouseEnter,
	updatePosition,
}) => {
	const popupContainerRef = useRef<HTMLDivElement | null>(null);
	useAdjustedTop(updatePosition, popupContainerRef);
	const isNewToolbarEnabled =
		fg('aifc_create_enabled') ||
		expValEquals('platform_editor_toolbar_aifc_confluence', 'isEnabled', true);

	const popupRef = useRef<HTMLDivElement | null>(null);
	const popupLeft = useAdjustedLeft(left, popupRef);

	const { formatMessage } = useIntl();

	useEffect(() => {
		const portalContainer = document.getElementById(popupPortalContainerId);
		if (!portalContainer) return;

		const screenreaderCb: MutationCallback = (_mutationsList, observer) => {
			//These accessibility attributes are added dynamically here rather than directly on the VisuallyHidden element because it did not get read by the NVDA screenreader properly on Firefox. This is due to the screenreader sometimes only reading content when there has been a dynamic change to an element
			const accessibleDiv = document.getElementById('toolbar-alert');
			if (accessibleDiv instanceof HTMLElement) {
				accessibleDiv?.setAttribute('role', 'alert');
				accessibleDiv?.setAttribute('aria-live', 'polite');
				observer.disconnect();
			}
		};

		const observer = new MutationObserver(screenreaderCb);
		observer.observe(portalContainer, { childList: true, subtree: true });

		return () => {
			observer.disconnect();
		};
	}, []);

	const portalContainer = document.getElementById(popupPortalContainerId);
	const mouseMoveHandlers = onMouseEnter && onMouseLeave && { onMouseEnter, onMouseLeave };

	return portalContainer
		? ReactDOM.createPortal(
				<div
					ref={popupContainerRef}
					style={{ top }}
					{...mouseMoveHandlers}
					css={[popupPanelStyles, needTransition && popupTransition]}
				>
					<VisuallyHidden id="toolbar-alert">{formatMessage(i18n.toolbarAlert)}</VisuallyHidden>
					{isNewToolbarEnabled ? (
						<div
							css={toolbarContainerStyle}
							ref={popupRef}
							style={{ left: `${popupLeft || 0}px` }}
							data-testid="highlightActionsPopup"
							id="highlightActionsPopup"
						>
							<Toolbar label="Renderer toolbar">{children}</Toolbar>
						</div>
					) : (
						<div
							css={[
								buttonContainerStyle,
								fg('confluence_comment_border_migration') && buttonContainerBorderUpdated,
							]}
							ref={popupRef}
							style={{ left: `${popupLeft || 0}px` }}
							data-testid="highlightActionsPopup"
							id="highlightActionsPopup"
						>
							{children}
						</div>
					)}
				</div>,
				portalContainer,
			)
		: null;
};

function useAdjustedLeft(
	left: PopupProps['left'],
	popupRef: MutableRefObject<HTMLDivElement | null>,
) {
	const WINDOW_PADDING = 20;
	const [adjustedLeft, setAdjustedLeft] = useState(left);
	const [adjustedLeftOrigin, setAdjustedLeftOrigin] = useState(left);

	useLayoutEffect(() => {
		if (!popupRef.current) return;

		const { left: elementLeft, right } = getElementBounds(popupRef.current);
		const windowWidth = window.innerWidth;

		let containerBounds = { left: 0, right: windowWidth }; // When nav4 container doesn't exist, the window is the container

		const nav4ContentContainer = document.getElementById('AkMainContent');
		if (nav4ContentContainer) {
			containerBounds = getElementBounds(nav4ContentContainer);
		}

		// WS-1703 - If the popup menu is off either side of the screen or outside of the scrollable content area
		// set it so it's inside the WINDOW_PADDING
		if (elementLeft < containerBounds.left) {
			setAdjustedLeft(left + (containerBounds.left - elementLeft) + WINDOW_PADDING);
		} else if (right > containerBounds.right) {
			setAdjustedLeft(left - (right - containerBounds.right) - WINDOW_PADDING);
		} else {
			setAdjustedLeft(left);
		}
		setAdjustedLeftOrigin(left);
	}, [left, popupRef]);

	// Only use the adjustedLeft if it was calculated relative to the current left prop
	return adjustedLeftOrigin === left ? adjustedLeft : left;
}

/**
 * `popupPortalContainerId` is after the ADF renderer in the HTML DOM.
 * Consequently, when there is vertical layout shift before `popupPortalContainerId` such as in the ADF renderer, the `Popup` will automatically shift away from the selected range.
 * This automatic shift is undesirable and `useAdjustedTop` is attempting to undo it in a way as imperceptible to the user as possible.
 *
 * Inspired by {@link https://floating-ui.com/} which is "The Future of Popper". Both Popper and Floating UI are used by Design System to deal with popups including to reposition them.
 */
function useAdjustedTop(
	updatePosition: PopupProps['updatePosition'],
	popupContainerRef: MutableRefObject<HTMLDivElement | null>,
) {
	const portalContainer = document.getElementById(popupPortalContainerId);

	useEffect(() => {
		if (!portalContainer || !updatePosition || !('ResizeObserver' in window)) return;

		// The position of `popupPortalContainerId` relative to the selected range will change when the parent element of `popupPortalContainerId` is resized.
		const { parentElement } = portalContainer;
		if (!parentElement) return;

		const observer = new ResizeObserver(() => {
			if (!popupContainerRef.current) return;

			const newPosition = updatePosition();
			if (!newPosition) return;

			const { style } = popupContainerRef.current;
			const newTop = `${newPosition.top}px`;
			if (style.top !== newTop) {
				// `popupPortalContainerId` moved and automatically shifted `popupContainerRef` away from the selected range.
				// This automatic shift is undesirable and here we're attempting to undo it in a way as imperceptible to the user as possible.
				// Imperceptible here means that a transition animation cannot be used.
				style.transition = 'none';
				style.top = newTop;
			}
		});
		observer.observe(parentElement);

		return () => {
			observer.disconnect();
		};
	}, [popupContainerRef, portalContainer, updatePosition]);
}
